import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  Pagination,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import {
  ArrowUpwardSharp,
  Close,
  DeleteOutline,
  EditOutlined,
  Lock,
} from "@mui/icons-material";
import { useMemo, useState } from "react";
import Icon from "src/components/common/Icons/Icons/Icon";
import AddUserModal from "src/components/Modals/AddUserModal";
import UpdatePasswordModal from "src/components/Modals/UpdatePasswordModal";
import UpdateSubjectsModal from "src/components/Modals/UpdateSubjectsModal";
import { UserRole } from "src/enums/User";
import { UserManagement } from "src/interfaces";

interface ChildUser extends UserManagement {
  username?: string | null;
  parentId?: number;
}

const TableRow = ({
  item,
  index,
  children,
  handlePress,
  onEdit,
  onDelete,
  loading,
}: {
  key: number;
  item: UserManagement;
  index: number;
  children?: ChildUser[];
  handlePress: (item: UserManagement) => void;
  onEdit: (item: UserManagement) => void;
  onDelete: (item: UserManagement) => void;
  loading: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div className="grid grid-cols-12 h-20 items-center px-2 hover:bg-gray-50">
        <div className="col-span-1 flex items-center">
          {children && children.length > 0 && (
            <ActionIcon onClick={() => setExpanded(!expanded)} size="sm">
              {expanded ? <Icon name="up" /> : <Icon name="down" />}
            </ActionIcon>
          )}
        </div>
        <div className="col-span-2">
          <Text
            size="xs"
            fw="600"
            style={{
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.email}
          </Text>
        </div>
        <div
          style={{
            maxHeight: 80,
            marginRight: 20,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "wrap",
          }}
          className="col-span-2"
        >
          <Text>{item.role == UserRole.parent ? "parent" : "user"}</Text>
        </div>
        <div className="col-span-1">
          <Text>{item.role !== UserRole.parent ? item.level || "—" : "—"}</Text>
        </div>
        <div className="col-span-1">
          <Text>{item.paid ? "Yes" : "No"}</Text>
        </div>
        <div className="col-span-2">
          <Text>{item.plan}</Text>
        </div>
        <div className="col-span-1 flex justify-center">
          <Switch
            checked={item.is_active}
            onChange={() => handlePress(item)}
            disabled={loading}
            size="sm"
            onLabel="On"
            offLabel="Off"
          />
        </div>
        <div className="col-span-2 flex justify-center items-center gap-2">
          <ActionIcon
            variant="subtle"
            size="sm"
            aria-label="Edit user"
            onClick={() => onEdit(item)}
            disabled={loading}
          >
            <EditOutlined fontSize="small" />
          </ActionIcon>
        </div>
      </div>

      {expanded &&
        children &&
        children.map((child, childIndex) => (
          <div
            key={childIndex}
            className="grid grid-cols-12 h-16 items-center px-2 bg-gray-50"
          >
            <div className="col-span-1">
              <Text ml={40}>•</Text>
            </div>
            <div className="col-span-2">
              <Text size="xs" fw="400">
                {child.username || child.email || "--"}
              </Text>
            </div>
            <div
              style={{
                maxHeight: 80,
                marginRight: 20,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "wrap",
              }}
              className="col-span-2"
            >
              <Text>{child.role == UserRole.parent ? "parent" : "user"}</Text>
            </div>
            <div className="col-span-1">
              <Text>
                {child.role !== UserRole.parent ? child.level || "—" : "—"}
              </Text>
            </div>
            <div className="col-span-1">
              <Text>{child.paid ? "Yes" : "No"}</Text>
            </div>
            <div className="col-span-2">
              <Text>{child.plan}</Text>
            </div>
            <div className="col-span-1 flex justify-center">
              <Switch
                checked={child.is_active}
                onChange={() => handlePress(child)}
                disabled={loading}
                size="sm"
                onLabel="On"
                offLabel="Off"
              />
            </div>
            <div className="col-span-2 flex justify-center items-center gap-2">
              <ActionIcon
                variant="subtle"
                size="sm"
                aria-label="Edit user"
                onClick={() => onEdit(child)}
                disabled={loading}
              >
                <EditOutlined fontSize="small" />
              </ActionIcon>
            </div>
          </div>
        ))}
    </>
  );
};

interface ScrollableTableProps {
  data: UserManagement[];
  onDeactivate: (item: UserManagement) => void;
  onPromoteLevel: (item: UserManagement) => Promise<boolean | void> | boolean | void;
  onDeleteUser: (item: UserManagement) => Promise<void> | void;
  onUpdateSubjects: (
    item: UserManagement,
    subjectIds: number[]
  ) => Promise<boolean | void> | boolean | void;
  onUpdatePassword: (
    item: UserManagement,
    password: string
  ) => Promise<void> | void;
  onCreateParent: (
    name: string,
    email: string,
    password: string,
    phone?: string
  ) => Promise<void> | void;
  onCreateLearner: (
    name: string,
    username: string,
    password: string,
    level: number,
    subjectIds: number[],
    school: string,
    dateOfBirth: string,
    parentEmail: string,
    avatarPath?: string
  ) => Promise<void> | void;
  loading: boolean;
}

export const UsersTable = (props: ScrollableTableProps) => {
  const {
    data,
    onDeactivate,
    onPromoteLevel,
    onDeleteUser,
    onUpdateSubjects,
    onUpdatePassword,
    onCreateParent: propsOnCreateParent,
    onCreateLearner: propsOnCreateLearner,
    loading,
  } = props;
  const theme = useMantineTheme();

  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedUser, setSelectedUser] = useState<UserManagement | null>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [subjectsModalOpen, setSubjectsModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState({
    promote: false,
    delete: false,
    subjects: false,
    password: false,
  });

  const organizedData = useMemo(() => {
    const parents = data.filter((user) => user.role === UserRole.parent);
    return parents.map((parent) => ({
      ...parent,
      children: data.filter(
        (user) => user.role !== UserRole.parent && user.parent_id === parent.id
      ) as ChildUser[],
    }));
  }, [data]);

  const filteredData = query
    ? organizedData.filter((item) => {
        const emailMatch = item.email
          .toLowerCase()
          .includes(query.toLowerCase());
        const childrenMatch = item.children?.some((child) => {
          const identifier = child.username || child.email || "";
          return identifier.toLowerCase().includes(query.toLowerCase());
        });
        return emailMatch || childrenMatch;
      })
    : organizedData;

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSearch = (value: string) => {
    setQuery(value);
    setCurrentPage(1);
  };

  const openProfileModal = (user: UserManagement) => {
    setSelectedUser(user);
    setSubjectsModalOpen(false);
    setProfileModalOpen(true);
  };

  const openDeleteModal = (user: UserManagement) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  const roleLabel = (role?: UserRole | null) => {
    if (role === UserRole.parent) return "Parent";
    if (role === UserRole.admin) return "Admin";
    if (role === UserRole.user) return "Learner";
    return "—";
  };

  const handlePromote = async () => {
    if (!selectedUser) return;
    setActionLoading((prev) => ({ ...prev, promote: true }));
    try {
      const success = await onPromoteLevel(selectedUser);
      if (success) {
        setProfileModalOpen(false);
      }
    } finally {
      setActionLoading((prev) => ({ ...prev, promote: false }));
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setActionLoading((prev) => ({ ...prev, delete: true }));
    try {
      await onDeleteUser(selectedUser);
      setDeleteModalOpen(false);
      setProfileModalOpen(false);
    } finally {
      setActionLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const profileFields = selectedUser
    ? [
        { label: "Email", value: selectedUser.email },
        { label: "Phone", value: selectedUser.phone_number || "—" },
        { label: "Role", value: roleLabel(selectedUser.role) },
        { label: "Level", value: selectedUser.level || "—" },
        { label: "Plan", value: selectedUser.plan || "—" },
        { label: "Paid", value: selectedUser.paid ? "Yes" : "No" },
        { label: "Trial Expiry", value: formatDate(selectedUser.trial_expiry) },
        {
          label: "Created At",
          value: formatDate(selectedUser.created_at || selectedUser.createdAt),
        },
        {
          label: "Walkthrough Completed",
          value: selectedUser.walkthrough_completed ? "Yes" : "No",
        },
        {
          label: "Child Preference",
          value: selectedUser.child_preference ?? "—",
        },
        { label: "Parent Id", value: selectedUser.parent_id ?? "—" },
        { label: "Username", value: selectedUser.username || "—" },
        { label: "Child School", value: selectedUser.child_school || "—" },
        {
          label: "Date of Birth",
          value: formatDate(selectedUser.date_of_birth),
        },
      ]
    : [];

  return (
    <>
      <Group align="space-between" className="mb-4">
        <TextInput
          placeholder="Search by email or username..."
          value={query}
          onChange={(event) => handleSearch(event.currentTarget.value)}
          className="flex-1"
        />
        <Button
          variant="filled"
          color="dark"
          className="flex gap-2"
          onClick={() => setAddUserModalOpen(true)}
        >
          Add Parent/Learner
        </Button>

        <AddUserModal
          opened={addUserModalOpen}
          onClose={() => setAddUserModalOpen(false)}
          parents={data.filter((u) => u.role === UserRole.parent)}
          onCreateParent={async (name, email, password, phone) => {
            await propsOnCreateParent(name, email, password, phone);
          }}
          onCreateLearner={async (
            name,
            username,
            password,
            level,
            subjectIds,
            school,
            dateOfBirth,
            parentEmail,
            avatarPath
          ) => {
            await propsOnCreateLearner(
              name,
              username,
              password,
              level,
              subjectIds,
              school,
              dateOfBirth,
              parentEmail,
              avatarPath
            );
          }}
        />
      </Group>
      <div className="md:overflow-visible overflow-x-auto my-4">
        <Card
          p={0}
          withBorder
          className="h-[400px] overflow-y-scroll flex flex-col rounded-lg relative min-w-[800px]"
        >
          <div
            className={`
              sticky top-0 flex flex-col z-40 px-3 py-3
              border-b border-gray-200
              ${theme.colorScheme === "dark" ? "bg-black" : "bg-gray-50"}
            `}
          >
          <div className="grid grid-cols-12 items-center text-xs uppercase tracking-wide text-gray-500">
            <div className="col-span-1">
              <Text size="xs" fw={600}>
                #
              </Text>
            </div>
            <div className="col-span-2">
              <Text size="xs" fw={600}>
                Email
              </Text>
            </div>
            <div className="col-span-2">
              <Text size="xs" fw={600}>
                User Type
              </Text>
            </div>
            <div className="col-span-1">
              <Text size="xs" fw={600}>
                Level
              </Text>
            </div>
            <div className="col-span-1">
              <Text size="xs" fw={600}>
                Paid
              </Text>
            </div>
            <div className="col-span-2">
              <Text size="xs" fw={600}>
                Plan
              </Text>
            </div>
            <div className="col-span-1 flex justify-center">
              <Text size="xs" fw={600}>
                Status
              </Text>
            </div>
            <div className="col-span-2 flex justify-center">
              <Text size="xs" fw={600}>
                Actions
              </Text>
            </div>
          </div>
        </div>
        {currentData.length > 0 ? (
          currentData.map((item, index) => (
            <div key={index}>
              <TableRow
                handlePress={onDeactivate}
                key={index}
                item={item}
                index={index}
                children={item.children}
                onEdit={openProfileModal}
                onDelete={openDeleteModal}
                loading={loading}
              />
              <Divider />
            </div>
          ))
        ) : (
          <div className="flex flex-1 justify-center items-center py-6">
            <Text fw={600} size="xs">
              No users found
            </Text>
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-50">
            <Loader color="dark" />
          </div>
        )}
        </Card>
      </div>
      <div className="w-full flex justify-center items-center">
        <Pagination
          total={totalPages}
          onChange={setCurrentPage}
          value={currentPage}
          color="dark"
          size="sm"
          radius="md"
          withControls={false}
          withEdges
        />
      </div>

      {/* Backdrop Overlay */}
      {profileModalOpen && (
        <div
          onClick={() => setProfileModalOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: 999,
            transition: "opacity 0.3s ease",
          }}
        />
      )}

      {/* Sidebar */}
      <div
        className="fixed top-0 h-screen bg-white z-[1000] flex flex-col transition-all duration-300 ease-in-out w-full md:w-[540px]"
        style={{
          right: profileModalOpen ? 0 : "-100%",
          boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.12)",
        }}
      >
        {selectedUser && (
          <>
            {/* Header */}
            <Box
              p="xl"
              style={{
                borderBottom: "1px solid #e9ecef",
                backgroundColor: "#fff",
              }}
            >
              <Group position="apart" mb="lg">
                <Text size="lg" fw={600} color="dark">
                  User Details
                </Text>
                <ActionIcon
                  size="lg"
                  variant="subtle"
                  onClick={() => setProfileModalOpen(false)}
                  style={{ color: "#495057" }}
                >
                  <Close />
                </ActionIcon>
              </Group>

              <Group>
                <Avatar
                  radius="xl"
                  size={64}
                  src={selectedUser.profile_picture_url || undefined}
                  style={{ border: "2px solid #e9ecef" }}
                >
                  <Text size="lg" fw={600} color="gray">
                    {selectedUser.name?.slice(0, 1)?.toUpperCase() || "U"}
                  </Text>
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Text size="lg" fw={600} mb={4}>
                    {selectedUser.name || "Unnamed user"}
                  </Text>
                  <Text size="sm" color="dimmed" mb={8}>
                    {selectedUser.email}
                  </Text>
                  <Group spacing={8}>
                    <Badge variant="outline" color="dark" size="md">
                      {selectedUser.plan || "No Plan"}
                    </Badge>
                    <Badge
                      variant={selectedUser.is_active ? "filled" : "outline"}
                      color={selectedUser.is_active ? "dark" : "gray"}
                      size="md"
                    >
                      {selectedUser.is_active ? "Active" : "Inactive"}
                    </Badge>
                    <Badge variant="outline" color="gray" size="md">
                      {roleLabel(selectedUser.role)}
                    </Badge>
                  </Group>
                </div>
              </Group>
            </Box>

            {/* Quick Actions */}
            <Box
              p="lg"
              style={{
                borderBottom: "1px solid #e9ecef",
                backgroundColor: "#f8f9fa",
              }}
            >
              <Text size="xs" fw={600} tt="uppercase" color="dimmed" mb="sm">
                Quick Actions
              </Text>
              <div className="flex gap-3">
                {selectedUser.role !== UserRole.parent && (
                  <>
                    <Button
                      variant="outline"
                      size="xs"
                      leftIcon={<EditOutlined style={{ fontSize: 14 }} />}
                      onClick={() => setSubjectsModalOpen(true)}
                    >
                      Edit Subjects
                    </Button>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={handlePromote}
                      loading={actionLoading.promote}
                      leftIcon={<ArrowUpwardSharp style={{ fontSize: 14 }} />}
                    >
                      Promote Level
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  size="xs"
                  leftIcon={<Lock style={{ fontSize: 14 }} />}
                  onClick={() => setPasswordModalOpen(true)}
                >
                  Update Password
                </Button>
              </div>
            </Box>

            {/* Scrollable Content */}
            <ScrollArea style={{ flex: 1, padding: "24px" }}>
              <Stack spacing="xl">
                {/* Account Information */}
                <Box>
                  <Text
                    size="xs"
                    fw={600}
                    tt="uppercase"
                    color="dimmed"
                    mb="md"
                  >
                    Account Information
                  </Text>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <Box>
                      <Text size="xs" color="dimmed" mb={4}>
                        Email
                      </Text>
                      <Text size="sm" fw={500}>
                        {selectedUser.email}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="xs" color="dimmed" mb={4}>
                        Phone
                      </Text>
                      <Text size="sm" fw={500}>
                        {selectedUser.phone_number || "—"}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="xs" color="dimmed" mb={4}>
                        Username
                      </Text>
                      <Text size="sm" fw={500}>
                        {selectedUser.username || "—"}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="xs" color="dimmed" mb={4}>
                        Date of Birth
                      </Text>
                      <Text size="sm" fw={500}>
                        {formatDate(selectedUser.date_of_birth)}
                      </Text>
                    </Box>
                  </div>
                </Box>

                <Divider />

                {/* Subscription */}
                <Box>
                  <Text
                    size="xs"
                    fw={600}
                    tt="uppercase"
                    color="dimmed"
                    mb="md"
                  >
                    Subscription
                  </Text>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                      alignItems: "start",
                    }}
                  >
                    <Box>
                      <Text size="xs" color="dimmed" mb={4}>
                        Plan
                      </Text>
                      <Badge variant="filled" color="dark" size="md">
                        {selectedUser.plan || "—"}
                      </Badge>
                    </Box>
                    <Box>
                      <Text size="xs" color="dimmed" mb={4}>
                        Payment Status
                      </Text>
                      <Badge
                        variant={selectedUser.paid ? "filled" : "outline"}
                        color="dark"
                        size="md"
                      >
                        {selectedUser.paid ? "Paid" : "Unpaid"}
                      </Badge>
                    </Box>
                    <Box style={{ gridColumn: "1 / -1" }}>
                      <Text size="xs" color="dimmed" mb={4}>
                        Trial Expiry
                      </Text>
                      <Text size="sm" fw={500}>
                        {formatDate(selectedUser.trial_expiry)}
                      </Text>
                    </Box>
                  </div>
                </Box>

                <Divider />

                {/* Learning Profile */}
                <Box>
                  <Text
                    size="xs"
                    fw={600}
                    tt="uppercase"
                    color="dimmed"
                    mb="md"
                  >
                    Learning Profile
                  </Text>
                  <Stack spacing="md">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <Box>
                        <Text size="xs" color="dimmed" mb={4}>
                          Current Level
                        </Text>
                        <Badge variant="outline" color="dark" size="md">
                          {selectedUser.level || "Not Set"}
                        </Badge>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed" mb={4}>
                          School
                        </Text>
                        <Text size="sm" fw={500}>
                          {selectedUser.child_school || "—"}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed" mb={4}>
                          Child Preference
                        </Text>
                        <Text size="sm" fw={500}>
                          {selectedUser.child_preference ?? "—"}
                        </Text>
                      </Box>
                      {selectedUser.parent_id && (
                        <Box>
                          <Text size="xs" color="dimmed" mb={4}>
                            Parent ID
                          </Text>
                          <Text size="sm" fw={500}>
                            {selectedUser.parent_id}
                          </Text>
                        </Box>
                      )}
                    </div>
                  </Stack>
                </Box>

                <Divider />

                {/* Subjects */}
                <Box>
                  <Text
                    size="xs"
                    fw={600}
                    tt="uppercase"
                    color="dimmed"
                    mb="md"
                  >
                    Subjects
                  </Text>
                  <Box>
                    {selectedUser.subjects &&
                    selectedUser.subjects.length > 0 ? (
                      <Group spacing="xs">
                        {selectedUser.subjects.map((subject, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            color="dark"
                            size="md"
                          >
                            {subject}
                          </Badge>
                        ))}
                      </Group>
                    ) : (
                      <Text size="sm" color="dimmed" fs="italic">
                        No subjects assigned
                      </Text>
                    )}
                  </Box>
                </Box>

                <Divider />

                {/* Additional Information */}
                <Box>
                  <Text
                    size="xs"
                    fw={600}
                    tt="uppercase"
                    color="dimmed"
                    mb="md"
                  >
                    Additional Information
                  </Text>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <Box>
                      <Text size="xs" color="dimmed" mb={4}>
                        Walkthrough Status
                      </Text>
                      <Badge
                        variant={
                          selectedUser.walkthrough_completed
                            ? "filled"
                            : "outline"
                        }
                        color="dark"
                        size="md"
                      >
                        {selectedUser.walkthrough_completed
                          ? "Completed"
                          : "Not Completed"}
                      </Badge>
                    </Box>
                    <Box>
                      <Text size="xs" color="dimmed" mb={4}>
                        Account Created
                      </Text>
                      <Text size="sm" fw={500}>
                        {formatDate(
                          selectedUser.created_at || selectedUser.createdAt
                        )}
                      </Text>
                    </Box>
                  </div>
                </Box>
              </Stack>
            </ScrollArea>

            {/* Footer */}
            <Box
              p="lg"
              style={{
                borderTop: "1px solid #e9ecef",
                backgroundColor: "#fafafa",
              }}
            >
              <Button
                variant="default"
                onClick={() => setProfileModalOpen(false)}
                fullWidth
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </div>

      <UpdateSubjectsModal
        opened={subjectsModalOpen}
        onClose={() => setSubjectsModalOpen(false)}
        user={selectedUser}
        onUpdate={async (subjectIds) => {
          if (!selectedUser) return;
          setActionLoading((prev) => ({ ...prev, subjects: true }));
          try {
            const success = await onUpdateSubjects(selectedUser, subjectIds);
            if (success) {
              setSubjectsModalOpen(false);
              setProfileModalOpen(false);
            }
          } finally {
            setActionLoading((prev) => ({ ...prev, subjects: false }));
          }
        }}
      />

      <UpdatePasswordModal
        opened={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        user={selectedUser}
        onUpdate={async (password) => {
          if (!selectedUser) return;
          setActionLoading((prev) => ({ ...prev, password: true }));
          try {
            await props.onUpdatePassword(selectedUser, password);
            setPasswordModalOpen(false);
          } finally {
            setActionLoading((prev) => ({ ...prev, password: false }));
          }
        }}
      />

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete account?"
        centered
      >
        <Stack>
          <Text size="sm">
            This action is not reversible. The parent/learner will lose access.
          </Text>
          <Group position="right" mt="md">
            <Button variant="default" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDelete}
              loading={actionLoading.delete}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
